import React, {useCallback} from 'react';

import {composeFlowchartEventMetadata} from 'performance/processProcessesEvents';
import {ProcessesPluginBase} from 'processes/graph-builder-processes/platform/ProcessesPluginBase';

import {countEdgesByType} from '../processFlowchartConfigEditorConstants';
import {FlowchartEdgeChangeType, FlowchartNodeChangeType} from '../processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {FlowchartConfigMetadata} from '../redux/reduceFlowchartConfig';
import {applyFlowchartChangesCore} from './applyFlowchartChangesCore';
import {FlowchartConfigWithPartialMetadata} from './BaseFlowchartConfigStateManager';
import {ApplyFlowchartChangesFn, FlowchartChangeGenerator} from './FlowchartStateTypes';
import {detectProfilerNodeChanges, logNodeChangeProfilerEvents} from './useFlowchartStateProfiler';

interface UseApplyFlowchartChangesParams {
  setFlowchartAndMetadata: (
    updater: (currentState: FlowchartConfigWithMetadata) => FlowchartConfigWithPartialMetadata | null,
    saveImmediately?: boolean,
    skipSettingDirtyState?: boolean
  ) => Promise<FlowchartConfigMetadata | null>;
  setNodePositions: React.Dispatch<React.SetStateAction<Record<string, {x: number; y: number}>>>;
  nodeIdsToRefreshRef: React.MutableRefObject<Set<string>>;
  plugin: ProcessesPluginBase;
  ptdvPairToTransitionIdIndex: Record<string, Array<string>>;
  shouldIncrementNodeNames: boolean;
  locked: boolean;
  flowchartTypeName: string;
  flowchartConfigId: string;
}

/**
 * Returns the primary `applyFlowchartChanges` callback used across the flowchart
 * state machinery. Handles locking, profiler events, and tracking nodes that
 * need their ReactFlow internals refreshed after REPLACE changes.
 */
export function useApplyFlowchartChanges({
  setFlowchartAndMetadata,
  setNodePositions,
  nodeIdsToRefreshRef,
  plugin,
  ptdvPairToTransitionIdIndex,
  shouldIncrementNodeNames,
  locked,
  flowchartTypeName,
  flowchartConfigId,
}: UseApplyFlowchartChangesParams): ApplyFlowchartChangesFn {
  return useCallback(
    (
      changeGenerator: FlowchartChangeGenerator,
      saveImmediately: boolean = false,
      skipSettingDirtyState: boolean = false
    ): Promise<FlowchartConfigMetadata | null> =>
      setFlowchartAndMetadata(
        (currentState) => {
          if (!currentState) {
            return null;
          }

          const changes = changeGenerator(currentState);

          // When locked, allow selection changes so the sidepanel can open in read-only mode,
          // but block all other changes.
          if (locked) {
            const onlySelection =
              (changes.nodeChanges ?? []).every((c) => c.type === FlowchartNodeChangeType.SELECT) &&
              (changes.edgeChanges ?? []).every((c) => c.type === FlowchartEdgeChangeType.SELECT) &&
              !(changes.transitionChanges?.length ?? 0);
            if (!onlySelection) {
              return null;
            }
          }

          // When nodes are replaced (e.g., field reordering), handle positions might change.
          // Track these nodes so their edges get recalculated.
          const replacedNodeIds = (changes.nodeChanges ?? [])
            .filter((c) => c.type === FlowchartNodeChangeType.REPLACE)
            .map((c) => c.id);

          if (replacedNodeIds.length > 0) {
            replacedNodeIds.forEach((nodeId) => nodeIdsToRefreshRef.current.add(nodeId));
          }

          const profilerFlags = detectProfilerNodeChanges(
            changes.nodeChanges ?? [],
            currentState.staticFlowchart.nodesStaticData
          );
          logNodeChangeProfilerEvents(profilerFlags, 'start');

          const result = applyFlowchartChangesCore(
            currentState,
            changes,
            setNodePositions,
            plugin,
            ptdvPairToTransitionIdIndex,
            shouldIncrementNodeNames
          );

          const flowchartForProfilerMetadata = result?.staticFlowchart ?? currentState.staticFlowchart;

          logNodeChangeProfilerEvents(
            profilerFlags,
            'end',
            composeFlowchartEventMetadata({
              flowchartType: flowchartTypeName,
              numNodes: flowchartForProfilerMetadata.nodesStaticData.length,
              ...countEdgesByType(flowchartForProfilerMetadata.edgesStaticData),
              flowchartConfigId,
            })
          );

          return result;
        },
        saveImmediately,
        skipSettingDirtyState
      ),
    [
      setFlowchartAndMetadata,
      setNodePositions,
      nodeIdsToRefreshRef,
      locked,
      plugin,
      ptdvPairToTransitionIdIndex,
      shouldIncrementNodeNames,
      flowchartTypeName,
      flowchartConfigId,
    ]
  );
}
